import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  UserKeys,
  getAllSharedDrivesForUser,
  getDriveFoldersForTab,
} from '../../../../api/users-api';
import SharedDrivesFolderTree from './shared-drives/shared-drives-folder-tree';
import SharedDrivesGrid from './shared-drives/shared-drives-parent/shared-drives-grid';
import SharedDrivesChildGrid from './shared-drives/shared-drives-child/shared-drives-child-grid';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { SharedDriveModel } from '../../../../../types';
import SharedDrivesPermissionCheckDialog from './shared-drives/shared-drives-parent/shared-drives-permission-check-dialog';
import { useUserSettings } from '../../../../utils/use-user-settings';
import ChangeLogDialog from '../../../../shared/change-log-dialog';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';

interface SharedDriveGridWithTreeProps {
  userEmail: string;
  primaryDomain: string;
  driveRoles: string[];
  changeLogRoles: string[];
}

export default function SharedDriveGridWithTree({
  userEmail,
  primaryDomain,
  driveRoles,
  changeLogRoles,
}: SharedDriveGridWithTreeProps) {
  const [selectedFolder, setSelectedFolder] = useState('shared-drives');
  const [openPermissionCheckDialog, setOpenPermissionCheckDialog] =
    useState(false);
  const [permissionCheckLoading, setPermissionCheckLoading] = useState(false);

  const [selectedDrive, setSelectedDrive] = useState<SharedDriveModel | null>(
    null
  );

  const { loggedInUser, viewType } = useUserSettings();

  const requiresChangeLog = isAbleToSeeUIElement(
    changeLogRoles,
    DriveRoles.ViewSharedDrives.value
  );

  const [changeLogDialogOpen, setChangeLogDialogOpen] = useState(
    requiresChangeLog && userEmail !== ''
  );
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [submittedChangeLogMessage, setSubmittedChangeLogMessage] =
    useState('');

  const [paginationModel, setPaginationModel] = useState<{
    column: string;
    sortDirection: 'ASC' | 'DESC';
  }>({
    column: 'name',
    sortDirection: 'ASC',
  });

  const {
    data: allFolders,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [UserKeys.SharedDriveFolders, loggedInUser],
    queryFn: ({ pageParam }) => {
      return getDriveFoldersForTab(
        loggedInUser,
        pageParam,
        false,
        false,
        false,
        false,
        'name'
      );
    },
    initialPageParam: selectedFolder,
    getNextPageParam: () => {
      return selectedFolder;
    },
    enabled: selectedFolder !== 'shared-drives',
    meta: {
      errorMessage:
        'There was an issue loading drive folders. Please refresh and try again.',
    },
  });

  useEffect(() => {
    if (
      selectedFolder !== 'shared-drives' &&
      !isFetchingNextPage &&
      !allFolders?.pageParams.includes(selectedFolder)
    ) {
      fetchNextPage();
    }
  }, [
    allFolders?.pageParams,
    fetchNextPage,
    isFetchingNextPage,
    selectedFolder,
  ]);

  const {
    data: sharedDrives,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      UserKeys.SharedDrivesForUser,
      userEmail,
      submittedChangeLogMessage,
    ],
    queryFn: () =>
      getAllSharedDrivesForUser(userEmail, submittedChangeLogMessage),
    enabled:
      userEmail !== '' &&
      (!requiresChangeLog || submittedChangeLogMessage !== ''),
    meta: {
      errorMessage:
        'There was an issue loading shared drives. Please refresh and try again.',
    },
  });

  return (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      sx={{ height: '100%', p: 0 }}
      gap={2}
    >
      <Card sx={{ width: 250 }}>
        <SharedDrivesFolderTree
          userEmail={loggedInUser}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          allFolders={allFolders}
          isLoading={false}
          sharedDrives={sharedDrives || []}
          setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
          setPermissionCheckLoading={setPermissionCheckLoading}
          setSelectedDrive={setSelectedDrive}
          permissionCheckLoading={permissionCheckLoading}
        />
      </Card>
      {selectedFolder === 'shared-drives' ? (
        <SharedDrivesGrid
          key={viewType.sharedDrivesGridStates?.activeGridState || 'default'}
          userEmail={loggedInUser}
          isLoading={isLoading || isFetching}
          sharedDrives={sharedDrives}
          primaryDomain={primaryDomain}
          driveRoles={driveRoles}
          changeLogRoles={changeLogRoles}
          setSelectedFolder={setSelectedFolder}
          selectedDrive={selectedDrive}
          setSelectedDrive={setSelectedDrive}
          setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
          permissionCheckLoading={permissionCheckLoading}
          setPermissionCheckLoading={setPermissionCheckLoading}
          showChangeLogOverlay={
            requiresChangeLog && submittedChangeLogMessage === ''
          }
          onOpenChangeLogDialog={() => setChangeLogDialogOpen(true)}
        />
      ) : (
        <SharedDrivesChildGrid
          userEmail={loggedInUser}
          primaryDomain={primaryDomain}
          driveRoles={driveRoles}
          changeLogRoles={changeLogRoles}
          isLoading={isLoading || isFetching}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          sharedDrives={sharedDrives || []}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
        />
      )}
      {selectedDrive && openPermissionCheckDialog ? (
        <SharedDrivesPermissionCheckDialog
          open={openPermissionCheckDialog}
          handleClose={() => {
            setOpenPermissionCheckDialog(false);
            setSelectedDrive(null);
          }}
          minimumRequiredRole="reader"
          folderOrDriveId={selectedDrive.id}
          actionAfterPermissionGranted={() => {
            setSelectedFolder(selectedDrive.id);
            setSelectedDrive(null);
          }}
          nameOfSharedDrive={selectedDrive.name}
        />
      ) : null}
      {changeLogDialogOpen ? (
        <ChangeLogDialog
          open={changeLogDialogOpen}
          handleClose={() => {
            setChangeLogDialogOpen(false);
          }}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            setSubmittedChangeLogMessage(changeLogMessage);
            setChangeLogDialogOpen(false);
          }}
          mutateLoading={isFetching}
        />
      ) : null}
    </Stack>
  );
}
